import { defineComponent as _defineComponent } from 'vue'

import { ColorPicker, Dropdown, DropdownMenu, DropdownItem, Input, Switch } from 'element-ui';
import { BPagination } from 'bootstrap-vue';
import FilterBar from '@/components/FilterBar.vue';
import InfoHint from '@/components/InfoHint.vue';

const __default__ = {
  components: {
    [ColorPicker.name]: ColorPicker,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Input.name]: Input,
    [Switch.name]: Switch,
    BPagination,
    FilterBar,
    InfoHint,
  },
};

import { COLOR_PRESETS } from '@/util/colorPresets';
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import swal from 'sweetalert2';
import { API_LOCATION } from '@/api-config';

interface Tag {
  id?: number;
  name: string;
  description: string | null;
  color: string;
  is_archived?: number;
  also_tag_customers: number | boolean;
  apply_to_online_bookings?: number | boolean;
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'TagLibraryTab',
  setup(__props, { expose }) {

const DEFAULT_COLOR = '#1d8cf8';

const companyId = localStorage.getItem('companyId');
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id;
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/tags`;

const loading = ref(false);
const saving = ref(false);
const tags = ref<Tag[]>([]);
const totalRows = ref(0);
const currentPage = ref(1);
const perPage = ref(10);
const perPageOptions = [10, 25, 50];
const filterValues = ref<{ name: string; archived: boolean }>({ name: '', archived: false });
const showDrawer = ref(false);
const isEditing = ref(false);
const selectedTag = ref<Tag>({ name: '', description: '', color: DEFAULT_COLOR, also_tag_customers: 0 });
const errs = ref({ name: '' });

const colorPresets = COLOR_PRESETS;

const filterSchema = computed(() => [
  { key: 'name', type: 'text', label: 'Name', placeholder: 'Search by name', operatorLabel: 'contains', primary: true },
  { key: 'archived', type: 'toggle', label: 'Show archived', primary: true },
]);

const hasActiveFilters = computed(() => !!filterValues.value.name || !!filterValues.value.archived);

const hasDownloadPermission = computed(() => {
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  return permissions.some((p: string) => p === 'download_data');
});

// Component instance proxy for $notify/$router access from setup scope.
const vm = getCurrentInstance()!.proxy as any;

async function getAuthToken() {
  const user = await Auth.currentAuthenticatedUser();
  return user.signInUserSession.idToken.jwtToken;
}

function buildListUrl(count: number, offset: number) {
  let url = `${baseUrl}?count=${count}&offset=${offset}`;
  if (!filterValues.value.archived) url += '&archived=0';
  if (filterValues.value.name) url += `&name=${encodeURIComponent(filterValues.value.name)}`;
  return url;
}

async function getTags() {
  loading.value = true;
  try {
    const token = await getAuthToken();
    const config = { headers: { Authorization: token } };
    const response = await axios.get(buildListUrl(perPage.value, (currentPage.value - 1) * perPage.value), config);
    tags.value = response.data.tags || [];
    totalRows.value = response.data.total_rows || 0;
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      vm.$router.push('/forbidden');
    } else {
      vm.$notify({ type: 'danger', message: 'Failed to load tags', timeout: 3000 });
    }
  } finally {
    loading.value = false;
  }
}

function onFiltersChanged(values: any) {
  filterValues.value = { name: values.name || '', archived: !!values.archived };
  currentPage.value = 1;
  getTags();
}

function onPerPageChange(value: number) {
  perPage.value = value;
  currentPage.value = 1;
  getTags();
}

function openAddForm() {
  isEditing.value = false;
  selectedTag.value = { name: '', description: '', color: DEFAULT_COLOR, also_tag_customers: 0, apply_to_online_bookings: 0 };
  errs.value.name = '';
  showDrawer.value = true;
}

function openEditDrawer(tag: Tag) {
  isEditing.value = true;
  selectedTag.value = {
    id: tag.id,
    name: tag.name,
    description: tag.description || '',
    color: tag.color || DEFAULT_COLOR,
    is_archived: tag.is_archived ? 1 : 0,
    also_tag_customers: tag.also_tag_customers ? 1 : 0,
    apply_to_online_bookings: tag.apply_to_online_bookings ? 1 : 0,
  };
  errs.value.name = '';
  showDrawer.value = true;
}

function closeDrawer() {
  showDrawer.value = false;
  isEditing.value = false;
  errs.value.name = '';
}

// Backend error bodies are JSON ({ error }); fall back to a generic message.
function apiErrorMessage(err: any, fallback: string) {
  const data = err?.response?.data;
  if (data && typeof data === 'object' && data.error) return data.error;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (parsed && parsed.error) return parsed.error;
    } catch (e) { /* not JSON */ }
  }
  return fallback;
}

async function saveTag() {
  if (!selectedTag.value.name || !selectedTag.value.name.trim()) {
    errs.value.name = 'Name is required';
    return;
  }
  errs.value.name = '';
  saving.value = true;

  try {
    const token = await getAuthToken();
    const config = { headers: { Authorization: token } };
    const data: any = {
      name: selectedTag.value.name.trim(),
      description: selectedTag.value.description ? String(selectedTag.value.description).trim() : null,
      color: selectedTag.value.color || DEFAULT_COLOR,
      also_tag_customers: selectedTag.value.also_tag_customers ? 1 : 0,
      apply_to_online_bookings: selectedTag.value.apply_to_online_bookings ? 1 : 0,
    };

    if (isEditing.value) {
      await axios.put(`${baseUrl}/${selectedTag.value.id}`, data, config);
      vm.$notify({ type: 'success', message: 'Tag updated', timeout: 3000 });
    } else {
      await axios.post(baseUrl, data, config);
      vm.$notify({ type: 'success', message: 'Tag created', timeout: 3000 });
    }

    closeDrawer();
    await getTags();
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      vm.$router.push('/forbidden');
    } else if (err.response && err.response.status === 400) {
      errs.value.name = apiErrorMessage(err, 'Could not save the tag');
    } else {
      swal('Error', 'Something went wrong', 'error');
    }
  } finally {
    saving.value = false;
  }
}

async function archiveTag(tag: Tag) {
  const result = await swal({
    title: 'Are you sure?',
    text: `"${tag.name}" will be archived. Bookings and customers keep this tag; it just leaves the picker.`,
    type: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Archive',
    cancelButtonText: 'Cancel',
  });
  if (!result.value) return;

  loading.value = true;
  try {
    const token = await getAuthToken();
    const config = { headers: { Authorization: token } };
    await axios.delete(`${baseUrl}/${tag.id}`, config);
    vm.$notify({ type: 'success', message: 'Tag archived', timeout: 3000 });
    await getTags();
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      vm.$router.push('/forbidden');
    } else {
      swal('Error', 'Something went wrong', 'error');
    }
    loading.value = false;
  }
}

async function restoreTag(tag: Tag) {
  try {
    const token = await getAuthToken();
    const config = { headers: { Authorization: token } };
    await axios.put(`${baseUrl}/${tag.id}`, {
      name: tag.name,
      description: tag.description || null,
      color: tag.color || DEFAULT_COLOR,
      also_tag_customers: tag.also_tag_customers ? 1 : 0,
      is_archived: 0,
    }, config);
    vm.$notify({ type: 'success', message: 'Tag restored', timeout: 3000 });
    await getTags();
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      vm.$router.push('/forbidden');
    } else if (err.response && err.response.status === 400) {
      vm.$notify({ type: 'danger', message: apiErrorMessage(err, 'Could not restore the tag'), timeout: 4000 });
    } else {
      swal('Error', 'Something went wrong', 'error');
    }
  }
}

// Full export across pages, then a client-built CSV (house pattern).
async function downloadCSV() {
  try {
    const token = await getAuthToken();
    const config = { headers: { Authorization: token } };
    const all: Tag[] = [];
    let offset = 0;
    const batch = 100;
    for (;;) {
      const response = await axios.get(buildListUrl(batch, offset), config);
      const page = response.data.tags || [];
      all.push(...page);
      offset += batch;
      if (all.length >= (response.data.total_rows || 0) || page.length === 0) break;
    }

    const esc = (v: any) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    let csv = 'Name,Description,Color,Also tags customers,Applies to online bookings,Archived\n';
    all.forEach((t) => {
      csv += [esc(t.name), esc(t.description), esc(t.color), t.also_tag_customers ? 'Yes' : 'No', t.apply_to_online_bookings ? 'Yes' : 'No', t.is_archived ? 'Yes' : 'No'].join(',') + '\n';
    });

    const anchor = document.createElement('a');
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    anchor.target = '_blank';
    anchor.download = 'tags.csv';
    anchor.click();
  } catch (err) {
    vm.$notify({ type: 'danger', message: 'Failed to export tags', timeout: 3000 });
  }
}

onMounted(() => {
  getTags();
});

// The parent page's header row owns the Create tag and download buttons (they
// sit on the tab rail line); script-setup exposes nothing by default.
expose({ openAddForm, downloadCSV });

return { __sfc: true,DEFAULT_COLOR, companyId, companyGroupId, baseUrl, loading, saving, tags, totalRows, currentPage, perPage, perPageOptions, filterValues, showDrawer, isEditing, selectedTag, errs, colorPresets, filterSchema, hasActiveFilters, hasDownloadPermission, vm, getAuthToken, buildListUrl, getTags, onFiltersChanged, onPerPageChange, openAddForm, openEditDrawer, closeDrawer, apiErrorMessage, saveTag, archiveTag, restoreTag, downloadCSV, BPagination, FilterBar, InfoHint }
}

})